/**
 * Homepage. design.md 8.1.
 *
 * The blueprint rearranges the source blocks into design slots; it does not
 * rewrite them. Every string below is read from the block data through
 * `Ledger`, and whatever the layout has no slot for comes back from
 * `ledger.restWithContent()` and is rendered by the generic stream, so a block
 * can never be lost by a layout decision.
 *
 * Sections, top to bottom:
 *   1 hero  2 positioning + statistics  3 trust strip  4 contact block
 *   5 three pillars  6 "Starten Sie jetzt"  7 data security  8 CTA band
 */
import { useMemo, useRef, use, lazy, Suspense } from 'react';
import { useMarket } from '@/lib/market-context';
import {
  chrome,
  getImage,
  getPage,
  srcSetOf,
  toMediaPath,
  usePageBlocks,
  type ButtonBlockData,
  type CounterBlockData,
  type HeadingBlockData,
  type HtmlBlockData,
  type ImageBlockData,
} from '@/lib/content';
import {
  Ledger,
  blocksOf,
  firstHref,
  headingInner,
  listItems,
  listOfHtmlBlock,
  splitTopLevel,
  stripAnchors,
  type Part,
} from '@/lib/blueprint';
import { crumbsForPage } from '@/lib/breadcrumbs';
import { metaForPage } from '@/lib/head';
import { resolveHeadingLevels } from '@/lib/headings';
import { useReveal } from '@/lib/reveal';
import { UI } from '@/lib/ui-strings';
import { StatRow } from '@/components/blocks/CounterBlock';
import ContactActions from '@/components/ContactActions';
import CtaBand from '@/components/CtaBand';
import Head from '@/components/Head';
import Pledges from '@/components/Pledges';
import SectionHeader from '@/components/SectionHeader';
import SmartLink from '@/components/SmartLink';
import TrustStrip from '@/components/TrustStrip';
import {
  ArrowRightIcon,
  CheckIcon,
  MonitorIcon,
  PresentationIcon,
  ShieldIcon,
} from '@/components/Icons';
import NotFound from './NotFound';

// Account for the 3:2 source filling a 4:5 desktop frame: object-fit enlarges
// it by 1.875, so requesting only the frame width would produce a soft crop.
const HERO_SIZES =
  '(min-width: 80rem) 908px, (min-width: 64rem) calc(78.125vw - 1.5625 * clamp(1.25rem, 0.5rem + 3.3vw, 3rem) - 1.09375rem), calc(100vw - 2 * clamp(1.25rem, 0.5rem + 3.3vw, 3rem))';
const PILLAR_ICONS = [ShieldIcon, PresentationIcon, MonitorIcon];
// The normal homepage consumes all blocks in its dedicated sections. Load
// generic charts, forms and listing renderers only if extra content needs them.
const Blocks = lazy(() => import('@/components/blocks/Blocks'));

export default function Home() {
  const page = getPage('/');
  if (!page) return <NotFound />;
  return <HomeView />;
}

function HomeView() {
  const { germany } = useMarket();
  const page = getPage('/')!;
  const blocks = usePageBlocks(page);
  // 8.1.7: the six commitments are the /datensicherheit page's own list items.
  const securityBlocks = use(blocksOf('/datensicherheit'));
  const root = useRef<HTMLDivElement | null>(null);
  useReveal(root, [page.id]);

  const crumbs = useMemo(() => crumbsForPage(page), [page]);
  const meta = useMemo(() => metaForPage(page, crumbs), [page, crumbs]);
  // Not memoised: a blueprint consumes the ledger while it renders, so the
  // ledger has to be fresh on every render or a second render (hydration,
  // StrictMode, any state change above) would find every block already
  // claimed and lay out an empty page.
  const ledger = new Ledger(blocks);
  const levels = useMemo(() => resolveHeadingLevels(blocks, 2), [blocks]);

  /* --- Hero (8.1.1) ---------------------------------------------------- */
  const heroEyebrow = ledger.at<HeadingBlockData>(0, 'heading');
  const heroTitle = ledger.at<HeadingBlockData>(1, 'heading');
  const heroLede = ledger.at<HeadingBlockData>(2, 'heading');
  const entryOne = ledger.at<HeadingBlockData>(3, 'heading');
  const entryTwo = ledger.at<HeadingBlockData>(4, 'heading');
  const heroSupport = ledger.at<HeadingBlockData>(5, 'heading');
  const heroImage = ledger.at<ImageBlockData>(13, 'image');

  /* --- Positioning (8.1.2) --------------------------------------------- */
  const positioningTitle = ledger.at<HeadingBlockData>(6, 'heading');
  const positioningBody = ledger.at<HtmlBlockData>(7, 'html');
  const counters = [9, 10, 11, 12]
    .map((i) => ledger.at<CounterBlockData>(i, 'counter'))
    .filter((b): b is CounterBlockData => Boolean(b));

  /* --- Contact block (8.1.4) ------------------------------------------- */
  const contactTitle = ledger.at<HeadingBlockData>(14, 'heading');
  const contactText = ledger.at<HeadingBlockData>(15, 'heading');
  const contactButtons = [16, 17, 18]
    .map((i) => ledger.at<ButtonBlockData>(i, 'button'))
    .filter((b): b is ButtonBlockData => Boolean(b));

  /* --- Three pillars (8.1.5) ------------------------------------------- */
  const pillarsTitle = ledger.at<HeadingBlockData>(20, 'heading');
  const pillarsLede = ledger.at<HtmlBlockData>(21, 'html');
  const pillarsBody = ledger.at<HtmlBlockData>(22, 'html');

  /* --- Starten Sie jetzt (8.1.6) --------------------------------------- */
  const startTitle = ledger.at<HeadingBlockData>(24, 'heading');
  const startLede = ledger.at<HeadingBlockData>(25, 'heading');
  const startButton = ledger.at<ButtonBlockData>(26, 'button');

  /* --- Data security (8.1.7) ------------------------------------------- */
  const securityBlock = ledger.at<HeadingBlockData>(27, 'heading');
  const securityParts = useMemo(
    () => (securityBlock ? splitTopLevel(headingInner(securityBlock)) : []),
    [securityBlock],
  );
  const pledges = useMemo(() => listOfHtmlBlock(securityBlocks), [securityBlocks]);

  const contactHref = `mailto:${chrome.footer.address.email}`;
  const rest = ledger.restWithContent();

  const positioningParts = useMemo(
    () => (positioningBody ? splitTopLevel(positioningBody.html) : []),
    [positioningBody],
  );
  const pillars = useMemo(
    () => (pillarsBody ? readPillars(splitTopLevel(pillarsBody.html)) : null),
    [pillarsBody],
  );

  return (
    <>
      <Head meta={meta} />
      <div className="hp-page hp-page--home" data-route={page.route} lang={germany ? 'de-DE' : 'de-CH'} ref={root}>
        {/* 1. Hero. No reveal anywhere in it: the h1 and the image are the LCP
            candidates and any starting opacity below 1 delays them. */}
        <section className="hp-hero">
          <div className="hp-container">
            <div className="hp-grid hp-hero__grid">
              <div className="hp-col-7 hp-stack hp-hero__text">
                {heroEyebrow ? (
                  <p
                    className="hp-h2 hp-as-eyebrow"
                    dangerouslySetInnerHTML={{ __html: headingInner(heroEyebrow) }}
                  />
                ) : null}
                {heroTitle ? (
                  <h1
                    className="hp-display hp-hero__title"
                    dangerouslySetInnerHTML={{ __html: germany ? headingInner(heroTitle).replace('in der Schweiz', 'in Deutschland') : headingInner(heroTitle) }}
                  />
                ) : null}
                {heroLede ? (
                  <div className="hp-hero__lede hp-lede" dangerouslySetInnerHTML={{ __html: headingInner(heroLede) }} />
                ) : null}

                <ul className="hp-entry-list">
                  {entryOne ? (
                    <EntryRow block={entryOne} href="/screenings" />
                  ) : null}
                  {entryTwo ? (
                    <EntryRow
                      block={entryTwo}
                      href="/workshops-und-seminare-fuer-gesunde-mitarbeitende"
                    />
                  ) : null}
                  <li className="hp-entry hp-entry--highlight">
                    <SmartLink
                      to="/bgm-zahlen-schweiz"
                      className="hp-entry__link hp-entry__link--highlight"
                    >
                      <span>Komplette BGM Marktübersicht 2026</span>
                      <ArrowRightIcon className="hp-entry__arrow" />
                    </SmartLink>
                  </li>
                </ul>

                {heroSupport ? (
                  <p
                    className="hp-hero__support"
                    dangerouslySetInnerHTML={{ __html: headingInner(heroSupport) }}
                  />
                ) : null}

                <div className="hp-actions hp-hero__actions">
                  {contactHref ? (
                    <SmartLink to={contactHref} className="hp-button hp-button--primary">
                      {UI.contact}
                    </SmartLink>
                  ) : null}
                  <SmartLink to="/screenings" className="hp-button hp-button--secondary">
                    {UI.heroSecondary}
                  </SmartLink>
                </div>
              </div>

              {heroImage?.image?.src ? (
                <figure className="hp-col-5 hp-start-8 hp-hero__media">
                  <HeroImage block={heroImage} />
                </figure>
              ) : null}
            </div>
          </div>
        </section>

        {/* 2. Positioning, with the statistics sticky beside it. */}
        <section className="hp-section">
          <div className="hp-container">
            <div className="hp-grid">
              <div className="hp-col-7 hp-stack">
                <SectionHeader
                  eyebrow={UI.eyebrowPositioning}
                  titleHtml={positioningTitle ? headingInner(positioningTitle) : undefined}
                  level={2}
                />
                <div data-reveal className="hp-stack">
                  <PartsFlow parts={positioningParts} />
                </div>
              </div>
              {counters.length ? (
                <div className="hp-col-4 hp-start-9">
                  <StatRow
                    className="hp-stat-panel"
                    reveal
                    items={counters.map((c) => ({ value: c.value, label: c.label }))}
                  />
                </div>
              ) : null}
            </div>
          </div>
        </section>

        {/* 3. Trust strip. */}
        <TrustStrip />

        {/* 4. The source's mid-page contact block; it stays mid-page (8.1.4). */}
        <section id="kontakt-aufnehmen" style={{ scrollMarginTop: '120px' }} className="hp-section hp-section--tight hp-section--subtle">
          <div className="hp-container">
            <div className="hp-contact-block">
              <div className="hp-contact-block__text">
                {contactTitle ? (
                  <h2
                    className="hp-h2"
                    dangerouslySetInnerHTML={{ __html: headingInner(contactTitle) }}
                  />
                ) : null}
                {contactText ? (
                  <div className="hp-lede" dangerouslySetInnerHTML={{ __html: headingInner(contactText) }} />
                ) : null}
              </div>
              <ContactActions buttons={contactButtons} />
            </div>
          </div>
        </section>

        {/* 5. Three pillars. */}
        <section className="hp-section">
          <div className="hp-container">
            <SectionHeader
              eyebrow={UI.eyebrowApproach}
              titleHtml={pillarsTitle ? headingInner(pillarsTitle) : undefined}
              level={2}
            >
              {pillarsLede ? (
                <div
                  className="hp-lede hp-section-header__lede"
                  dangerouslySetInnerHTML={{ __html: pillarsLede.html }}
                />
              ) : null}
            </SectionHeader>

            {pillars ? (
              <div className="hp-pillars">
                {pillars.intro.length ? (
                  <div className="hp-pillars__intro">
                    {pillars.intro.map((part, i) =>
                      i === 0 ? (
                        <h3
                          className="hp-h3"
                          key={i}
                          dangerouslySetInnerHTML={{ __html: part.html }}
                        />
                      ) : (
                        <p key={i} dangerouslySetInnerHTML={{ __html: part.html }} />
                      ),
                    )}
                  </div>
                ) : null}

                <div className="hp-service-grid" data-reveal-group>
                  {pillars.cards.map((card, index) => {
                    const Icon = PILLAR_ICONS[index % PILLAR_ICONS.length];
                    return (
                      <article className="hp-service-card" key={index}>
                        <p className="hp-service-card__index" aria-hidden="true">
                          {index + 1}
                        </p>
                        <Icon width={32} height={32} className="hp-service-card__icon" />
                        <h4 className="hp-h4 hp-service-card__title">
                          <SmartLink to={card.href}>
                            <span dangerouslySetInnerHTML={{ __html: card.title }} />
                          </SmartLink>
                        </h4>
                        {/*
                          A div, not a p: `card.text` is the outer HTML of one
                          or more source paragraphs. Inside a <p> the HTML
                          parser closes the wrapper at the first nested <p>,
                          which splits the card apart in the prerendered
                          document and breaks hydration.
                        */}
                        <div
                          className="hp-service-card__text"
                          dangerouslySetInnerHTML={{ __html: card.text }}
                        />
                      </article>
                    );
                  })}
                </div>
              </div>
            ) : null}
          </div>
        </section>

        {/* 6. Starten Sie jetzt. */}
        <section className="hp-section hp-section--subtle">
          <div className="hp-container">
            <div className="hp-grid">
              <div className="hp-col-8 hp-stack">
                {startTitle ? (
                  <h2
                    className="hp-h4 hp-as-h2"
                    dangerouslySetInnerHTML={{ __html: headingInner(startTitle) }}
                  />
                ) : null}
                {startLede ? (
                  <div className="hp-h5 hp-as-lede" dangerouslySetInnerHTML={{ __html: headingInner(startLede) }} />
                ) : null}
                {startButton ? (
                  <div className="hp-actions">
                    <SmartLink to={startButton.url} className="hp-button hp-button--primary">
                      {startButton.text}
                    </SmartLink>
                  </div>
                ) : null}
              </div>
            </div>
          </div>
        </section>

        {/* 7. Data security: the condensed version of the strongest
            differentiator the client owns. */}
        <section className="hp-section">
          <div className="hp-container">
            <div className="hp-grid">
              <div className="hp-col-4 hp-stack">
                <p className="hp-eyebrow">{UI.eyebrowTrust}</p>
                {securityParts[0] ? (
                  <h2
                    className="hp-h5 hp-as-h2"
                    dangerouslySetInnerHTML={{ __html: securityParts[0].html }}
                  />
                ) : null}
              </div>
              <div className="hp-col-7 hp-start-6 hp-stack">
                {securityParts.slice(1).map((part, i) => (
                  <p
                    className="hp-lede"
                    key={i}
                    dangerouslySetInnerHTML={{ __html: part.html }}
                  />
                ))}
                <div data-reveal-group>
                  <Pledges items={pledges} />
                </div>
                <SmartLink to="/datensicherheit" className="hp-link-tertiary">
                  {UI.moreOnDataSecurity}
                  <ArrowRightIcon />
                </SmartLink>
              </div>
            </div>
          </div>
        </section>

        {/* Anything the blueprint has no slot for still reaches the DOM. */}
        {rest.length ? (
          <section className="hp-section hp-section--tight">
            <div className="hp-container">
              <Suspense fallback={null}>
                <Blocks blocks={rest} levels={levels} className="hp-blocks hp-page__blocks" />
              </Suspense>
            </div>
          </section>
        ) : null}

        <CtaBand />
      </div>
    </>
  );
}

/** One row of the hero's two-item entry list (8.1.1). */
function EntryRow({ block, href }: { block: HeadingBlockData; href: string }) {
  return (
    <li className="hp-entry">
      <SmartLink to={href} className="hp-entry__link">
        <span dangerouslySetInnerHTML={{ __html: headingInner(block) }} />
        <ArrowRightIcon className="hp-entry__arrow" />
      </SmartLink>
    </li>
  );
}

function HeroImage({ block }: { block: ImageBlockData }) {
  const src = toMediaPath(block.image!.src);
  const asset = getImage(src);
  return (
    <img
      className="hp-hero__img"
      src={asset?.sources.at(-1)?.url ?? src}
      alt={block.image!.alt ?? ''}
      {...(asset
        ? {
            srcSet: srcSetOf(asset),
            sizes: HERO_SIZES,
            width: asset.width,
            height: asset.height,
          }
        : null)}
      loading="eager"
      fetchPriority="high"
      decoding="async"
    />
  );
}

/**
 * The positioning column: paragraphs as prose, the source `<ul>` as the
 * check-list of 8.1.2. Every part of the source block is rendered, in order.
 */
function PartsFlow({ parts }: { parts: Part[] }) {
  return (
    <>
      {parts.map((part, i) => {
        if (part.tag === 'ul') {
          return (
            <ul className="hp-checklist" key={i}>
              {listItems(part.html).map((item, j) => (
                <li className="hp-checklist__item" key={j}>
                  <CheckIcon className="hp-checklist__icon" />
                  <span dangerouslySetInnerHTML={{ __html: item }} />
                </li>
              ))}
            </ul>
          );
        }
        return (
          <div
            className="hp-block hp-block--html prose"
            key={i}
            dangerouslySetInnerHTML={{ __html: part.tag ? part.outer : `<p>${part.html}</p>` }}
          />
        );
      })}
    </>
  );
}

type Pillar = { title: string; text: string; href: string | null };

/**
 * The "3 Säulen" block: an intro (a bold line and a sentence), then three
 * `<ol>` / `<p>` pairs, one per pillar. Read structurally, so the copy is
 * never matched on.
 */
function readPillars(parts: Part[]): { intro: Part[]; cards: Pillar[] } {
  const intro: Part[] = [];
  const cards: Pillar[] = [];
  for (let i = 0; i < parts.length; i += 1) {
    const part = parts[i];
    if (part.tag === 'ol') {
      const item = listItems(part.html)[0] ?? part.html;
      const next = parts[i + 1];
      const text = next && next.tag !== 'ol' ? next.outer : '';
      if (next && next.tag !== 'ol') i += 1;
      cards.push({ title: stripAnchors(item), text, href: firstHref(item) });
      continue;
    }
    if (cards.length === 0) intro.push(part);
    else cards[cards.length - 1].text += part.outer;
  }
  return { intro, cards };
}
